import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Shield, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Human-readable labels and descriptions for each granular preference. Order
// mirrors the backend `PREFERENCE_KEYS` list.
const LABELS = {
  weekly_newsletter: {
    title: 'Weekly Newsletter',
    desc: 'Our Chamber digest with news, events, member spotlights and deals.',
  },
  events: {
    title: 'Events',
    desc: 'Invitations and reminders for Chamber events, mixers and workshops.',
  },
  member_deals: {
    title: 'Member Deals',
    desc: 'New business offers and exclusive promotions from fellow members.',
  },
  chamber_news: {
    title: 'Chamber News & Announcements',
    desc: 'Important updates from the Chamber, Board and committees.',
  },
  community_updates: {
    title: 'Community Updates',
    desc: 'Highlights from the member community feed.',
  },
  marketing: {
    title: 'Marketing & Campaigns',
    desc: 'Occasional messages from Chamber partners and sponsors.',
  },
};

const EmailPreferencesPage = () => {
  const [prefs, setPrefs] = useState(null);
  const [keys, setKeys] = useState([]);
  const [suppressed, setSuppressed] = useState(false);
  const [suppressionReason, setSuppressionReason] = useState(null);
  const [saving, setSaving] = useState(null); // key currently being toggled
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/portal/email-preferences`, { withCredentials: true });
      setPrefs(data.preferences);
      setKeys(data.keys);
      setSuppressed(data.globally_suppressed);
      setSuppressionReason(data.suppression_reason);
    } catch (err) {
      toast.error('Could not load preferences');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (key, value) => {
    setSaving(key);
    setPrefs((prev) => ({ ...prev, [key]: value }));
    try {
      const { data } = await axios.patch(
        `${API}/portal/email-preferences`,
        { preferences: { [key]: value } },
        { withCredentials: true },
      );
      setPrefs(data.preferences);
      toast.success(value ? `Opted in to ${LABELS[key]?.title || key}` : `Opted out of ${LABELS[key]?.title || key}`);
    } catch (err) {
      // Revert optimistic update
      setPrefs((prev) => ({ ...prev, [key]: !value }));
      toast.error('Could not save — please try again');
    } finally {
      setSaving(null);
    }
  };

  const turnAll = async (on) => {
    setSaving('all');
    const next = Object.fromEntries(keys.map((k) => [k, on]));
    try {
      const { data } = await axios.patch(
        `${API}/portal/email-preferences`,
        { preferences: next },
        { withCredentials: true },
      );
      setPrefs(data.preferences);
      toast.success(on ? 'Subscribed to all Chamber emails' : 'Unsubscribed from all marketing emails');
    } catch (err) {
      toast.error('Could not save');
    } finally {
      setSaving(null);
    }
  };

  if (loading || !prefs) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="h-6 w-40 bg-slate-100 rounded animate-pulse mb-4" />
        <div className="space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-slate-50 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 sm:py-8" data-testid="email-preferences-page">
      <Link
        to="/portal/profile"
        className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4"
        data-testid="back-to-profile"
      >
        <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to profile
      </Link>

      <div className="flex items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#002249] text-white flex items-center justify-center flex-shrink-0">
          <Mail className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a1a]">Email Preferences</h1>
          <p className="text-sm text-slate-500 mt-1">
            Choose what you want to hear from the Chamber. You can change these anytime.
          </p>
        </div>
      </div>

      {suppressed && (
        <div
          className="flex items-start gap-3 p-3 mb-5 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm"
          data-testid="globally-suppressed-banner"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Marketing emails are currently paused for your address.</p>
            <p className="text-amber-800/80 mt-0.5">
              Reason: <span className="font-mono text-xs">{suppressionReason || 'suppression'}</span>.
              Toggle any preference below back on to resume receiving them.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-2.5">
        {keys.map((k) => {
          const meta = LABELS[k] || { title: k, desc: '' };
          const active = prefs[k] !== false;
          return (
            <div
              key={k}
              className={`flex items-start justify-between gap-4 p-4 rounded-xl border transition-colors ${
                active ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50'
              }`}
              data-testid={`pref-row-${k}`}
            >
              <div className="min-w-0">
                <div className="font-semibold text-[#1a1a1a]">{meta.title}</div>
                <div className="text-sm text-slate-500 mt-0.5">{meta.desc}</div>
              </div>
              <Switch
                checked={active}
                disabled={saving === k || saving === 'all'}
                onCheckedChange={(v) => toggle(k, v)}
                data-testid={`pref-toggle-${k}`}
              />
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex gap-3">
        <Button
          variant="outline"
          onClick={() => turnAll(true)}
          disabled={saving !== null}
          data-testid="subscribe-all-btn"
        >
          <CheckCircle2 className="w-4 h-4 mr-1.5" /> Subscribe to all
        </Button>
        <Button
          variant="outline"
          onClick={() => turnAll(false)}
          disabled={saving !== null}
          className="text-red-600 border-red-200 hover:bg-red-50"
          data-testid="unsubscribe-all-btn"
        >
          Unsubscribe from everything
        </Button>
      </div>

      <div className="mt-8 p-4 rounded-xl bg-slate-50 border border-slate-100">
        <div className="flex items-start gap-2.5">
          <Shield className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-slate-600 leading-relaxed">
            <span className="font-semibold text-slate-800">Account emails always arrive.</span>{' '}
            Password resets, security alerts, invitations and other account-critical
            messages are not affected by these settings.
          </p>
        </div>
      </div>

      {/* Deep-link to Account Settings (self-service email change) */}
      <div className="mt-3 p-4 rounded-xl bg-white border border-slate-200 flex items-center justify-between">
        <div className="min-w-0">
          <div className="font-semibold text-sm text-[#1a1a1a]">Account email</div>
          <div className="text-xs text-slate-500 mt-0.5 truncate">
            Change the email you use to sign in and receive account messages.
          </div>
        </div>
        <Link
          to="/portal/account"
          className="text-sm font-semibold text-[#002249] hover:underline flex-shrink-0"
          data-testid="go-to-account-settings"
        >
          Manage →
        </Link>
      </div>
    </div>
  );
};

export default EmailPreferencesPage;
